// R115 guest — shared plumbing for the Calendar's reservation / listing actions:
// reading the inspector's data defensively (the contract leaves the stay
// inspector's `data` shape to core — a Stay, or the GET /api/stays/:id Detail
// with `stay` inside), small caches with explicit invalidation, fetch helpers,
// and the hand-off keys other pages read. No message text or email is ever
// written to sessionStorage / localStorage from here; a phone only ever as the
// R111 one-shot 'qx_focus_phone' hand-off (Messaging reads and deletes it at
// once) when a booking has no PMS thread to hand over by id.
import type { CalendarCtx, EntityRef } from '../registry';
import type { Stay, Detail, Property } from '../types';

export type GuestDetail = Detail & {
  flagged: (Detail['flagged'] & { conversation_id?: string | null }) | null;
  links?: { hostaway_reservation?: string | null; hostaway_listing?: string | null; airbnb_url?: string | null } | null;
  special_offer?: SpecialOffer | null;
};
export interface SpecialOffer {
  state: string; flagged?: boolean; missing?: string[]; checked_at?: string | null;
  broken_out?: boolean | null; balanced?: boolean | null;
  resolved?: { note: string; by: string; at: string | null; stale: boolean } | null;
  recorded?: { status: string; at: string | null; stale: boolean } | null;
  money?: { total: number | null; included_sum: number | null; components: { name: string; amount: number }[] } | null;
}
export interface Scores { airbnb: { avg: number | null; n: number }; all: { avg: number | null; n: number }; hostaway: number | null }
export interface ListingCard {
  listing: { listing_id: string; name: string; full_name: string; bedrooms: number | null; max_guests: number | null; thumbnail_url: string | null; min_nights: number | null; status: string | null };
  scores: Scores | null; scores_error?: string | null;
  counts: { open_claims: number | null; open_resolutions: number | null; recovery_open: number | null };
  owner: { id: string; name: string } | null;
  links: { hostaway_listing: string | null; airbnb_url: string | null };
}
export interface ScoresLayer { by_listing?: Record<string, Scores>; unavailable?: boolean }
export interface SoLayer { by_stay?: Record<string, { state: string; missing: string[]; checked_at: string | null }>; counts?: Record<string, number>; unavailable?: boolean }

// ── reading the inspector's data ────────────────────────────────────────
const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
export function detailFrom(data: unknown): GuestDetail | null {
  return isObj(data) && isObj(data.stay) && isObj(data.guest) ? (data as GuestDetail) : null;
}
export function stayFrom(ref: EntityRef, data: unknown, ctx: CalendarCtx): Stay | null {
  const d = detailFrom(data);
  if (d && d.stay && String(d.stay.id) === String(ref.id)) return d.stay;
  if (isObj(data) && data.id != null && String(data.id) === String(ref.id) && data.check_in) return data as Stay;
  return (ctx.data && Array.isArray(ctx.data.stays) ? ctx.data.stays.find(s => String(s.id) === String(ref.id)) : null) || null;
}
export function propertyOf(ctx: CalendarCtx, listingId: string, d?: GuestDetail | null): Property | { listing_id: string; name: string; thumbnail_url: string | null } | null {
  return ctx.property(listingId) || (d && d.stay && d.stay.property ? d.stay.property : null) || null;
}
export function confirmationOf(d: GuestDetail | null): string | null {
  if (!d) return null;
  return (d.guest && d.guest.confirmation_code) || (d.details && d.details.confirmation_code) || null;
}
export const listingIdOf = (ref: EntityRef, data: unknown): string => {
  if (ref.kind === 'listing') return String(ref.id);
  if (isObj(data) && data.listing_id) return String(data.listing_id);
  return String(ref.listing_id || ref.id);
};

// ── fetch ───────────────────────────────────────────────────────────────
export interface GetResult<T = any> { ok: boolean; status: number; json: T | null }
export async function getJson<T = any>(url: string, signal?: AbortSignal): Promise<GetResult<T>> {
  try {
    const r = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    let json: any = null;
    try { json = await r.json(); } catch { json = null; }
    return { ok: r.ok, status: r.status, json };
  } catch {
    return { ok: false, status: 0, json: null };
  }
}
export async function postJson<T = any>(url: string, body: unknown): Promise<GetResult<T>> {
  try {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    let json: any = null;
    try { json = await r.json(); } catch { json = null; }
    return { ok: r.ok, status: r.status, json };
  } catch {
    return { ok: false, status: 0, json: null };
  }
}
export const errText = (r: { status: number; json: any }, what: string): string => {
  if (!r.status) return `${what} — the network didn't answer. Check the connection and try again.`;
  if (r.status === 404 && !(r.json && r.json.error)) return `${what} — this server doesn't have the R115 route yet (redeploy).`;
  if (r.status === 403) return `${what} — your role can't do this here.`;
  return `${what} — ${(r.json && (r.json.error || r.json.message)) || `HTTP ${r.status}`}`;
};

// ── small caches (ids → data; never message bodies) ─────────────────────
interface Entry<T> { at: number; data: T | null; status: number; err: any; p: Promise<T | null> | null }
export class Cache<T> {
  private m = new Map<string, Entry<T>>();
  constructor(private ttlMs: number, private load: (key: string) => Promise<GetResult<T>>) {}
  peek(key: string): { data: T | null; status: number } | null {
    const e = this.m.get(key);
    if (!e || e.p || Date.now() - e.at > this.ttlMs) return null;
    return { data: e.data, status: e.status };
  }
  get(key: string): Promise<T | null> {
    const e = this.m.get(key);
    if (e && e.p) return e.p;
    if (e && Date.now() - e.at <= this.ttlMs) return Promise.resolve(e.data);
    const entry: Entry<T> = { at: 0, data: null, status: 0, err: null, p: null };
    entry.p = this.load(key).then(r => {
      entry.status = r.status; entry.data = r.ok ? r.json : null; entry.err = r.ok ? null : r.json; entry.p = null;
      // R115 review: a failure is never SERVED (at 0 is already past the TTL, so peek() is null and get() reloads),
      // but it stays readable so the caller can say what went wrong (a 500 / 403 is not "the network didn't answer")
      entry.at = r.ok ? Date.now() : 0;
      return entry.data;
    });
    this.m.set(key, entry);
    return entry.p;
  }
  lastStatus(key: string): number { const e = this.m.get(key); return e ? e.status : 0; }
  /** The last answer for `key` (status + the error body of a failure) — for errText. */
  lastResult(key: string): { status: number; json: any } { const e = this.m.get(key); return e ? { status: e.status, json: e.err } : { status: 0, json: null }; }
  drop(key?: string): void { if (key == null) this.m.clear(); else this.m.delete(key); }
}

export const listingCards = new Cache<ListingCard>(2 * 60 * 1000, (lid) => getJson<ListingCard>(`/api/stays/listing/${encodeURIComponent(lid)}`));
export interface RecoveryTarget { id: string; kind: 'case' | 'ticket'; origin?: string; guest_name?: string; listing_name?: string; summary?: string; excerpt?: string; recovery_state: string; recovery_status?: string; recovery_outcome?: string | null; recovery_log?: any[]; smoothed_at?: string | null; ticket_id?: string | null; [k: string]: any }
/** reservation → its conversation id (ids only — the by-reservation guest block is dropped before caching). */
export interface ConvRef { conversation_id: string | null; configured: { pms?: boolean; sms?: boolean } | null; lookup_error: string | null }
export const convRefs = new Cache<ConvRef>(10 * 60 * 1000, async (hid) => {
  const r = await getJson(`/api/messaging/by-reservation/${encodeURIComponent(hid)}`);
  // a failed Hostaway lookup is not an answer — never cache "no conversation" from it
  if (r.ok && r.json && !r.json.conversation_id && r.json.lookup_error) return { ok: false, status: 502, json: { error: `Couldn't reach Hostaway to find this booking's thread (${String(r.json.lookup_error).slice(0, 120)})` } };
  return { ok: r.ok, status: r.status, json: r.ok && r.json ? { conversation_id: r.json.conversation_id ? String(r.json.conversation_id) : null, configured: r.json.configured || null, lookup_error: null } : r.json };
});
export const recoveryTargets = new Cache<{ reservation_id: string; target: RecoveryTarget | null }>(60 * 1000,
  (hid) => getJson(`/api/assetops/issues/guest-recovery/for-reservation/${encodeURIComponent(hid)}`));

// ── who is looking ──────────────────────────────────────────────────────
export function actorEmail(): string {
  try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String((p && p.email) || '').trim().toLowerCase(); } catch { return ''; }
}

// ── hand-offs other pages consume (one-shot sessionStorage keys; ids only) ──
const put = (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch { /* private mode: the page opens unfocused */ } };
export const handoff = {
  propertyDetails(ctx: CalendarCtx, lid: string): void { put('ob_open', JSON.stringify({ id: lid, tab: 'details', phases: [], src: 'calendar' })); ctx.go('onboarding'); },
  revenueManager(ctx: CalendarCtx, lid: string): void { put('workup_preselect', lid); put('workup_tab', 'propensity'); ctx.go('data-explorer'); },
  optimizer(ctx: CalendarCtx, lid: string): void { put('lo_preselect', lid); ctx.go('listing-quality', 'optimizer'); },
  reviews(ctx: CalendarCtx, listingName: string): void { put('rv_focus_review', JSON.stringify({ ids: [], listing_name: listingName })); ctx.go('reviews'); },
  propertySettings(ctx: CalendarCtx, lid: string): void { put('ps_focus', lid); ctx.go('portfolio', 'settings'); },
  specialOffers(ctx: CalendarCtx, hid: string, checkIn: string): void { put('so_focus', JSON.stringify({ hid, month: String(checkIn || '').slice(0, 7) })); ctx.go('accounting', 'special-offers'); },
  resolutions(ctx: CalendarCtx): void { ctx.go('accounting', 'resolutions'); },
  claims(ctx: CalendarCtx): void { ctx.go('remote-ops', 'claims'); },
  recovery(ctx: CalendarCtx, id: string | null): void { if (id) put('gr_focus', id); ctx.go('guest-recovery'); },
  // conversation id + reservation id only; the phone hand-off is the fallback when no thread is known
  messaging(ctx: CalendarCtx, o: { cid?: string | null; rid?: string | null; phone?: string | null }): void {
    if (o.cid) put('qx_focus_conv', JSON.stringify({ cid: o.cid, rid: o.rid || null }));
    else if (o.phone) put('qx_focus_phone', o.phone);
    ctx.go('guest-texts');
  },
};

// ── formatting ──────────────────────────────────────────────────────────
export function fmtWhen(iso: unknown): string {
  const s = String(iso || '');
  const ms = Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s) ? s.replace(' ', 'T') + 'Z' : s);
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })}`;
}
export const money = (n: unknown): string => (n == null || !Number.isFinite(Number(n)) ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
